import React, { useEffect, useMemo, useState } from 'react';
import type { Event, User } from '../types';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import CalendarIcon from './icons/CalendarIcon';
import { shareElsewhere } from '../utils/shareLink';

interface ShareEventPageProps {
  event: Event;
  currentUser: User | null;
  allUsers: User[];
  onClose: () => void;
  onShareEvent: (eventId: string, recipientId: string) => void;
}

const ShareEventPage: React.FC<ShareEventPageProps> = ({ event, currentUser, allUsers, onClose, onShareEvent }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const [elsewhereFeedback, setElsewhereFeedback] = useState<string | null>(null);

  const handleShareElsewhere = async () => {
    const result = await shareElsewhere({
      kind: 'event',
      id: event.id,
      title: event.name,
      text: `Check out ${event.name} on RIDAZ`,
    });
    if (result === 'copied') {
      setElsewhereFeedback('Link copied!');
      window.setTimeout(() => setElsewhereFeedback(null), 2500);
    } else if (result === 'unavailable') {
      setElsewhereFeedback("Couldn't share right now.");
      window.setTimeout(() => setElsewhereFeedback(null), 2500);
    }
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const shareableUsers = useMemo(() => {
    if (!currentUser) return [];
    const query = searchQuery.trim().toLowerCase();
    const otherUsers = allUsers.filter((user) => user.id !== currentUser.id && !user.isBot);
    if (!query) return otherUsers;
    return otherUsers.filter((user) => {
      const username = user.username?.toLowerCase() || '';
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      return username.includes(query) || firstName.includes(query) || lastName.includes(query);
    });
  }, [allUsers, currentUser, searchQuery]);

  const handleSendShare = (recipientId: string) => {
    onShareEvent(event.id, recipientId);
    setSentTo((prev) => new Set(prev).add(recipientId));
  };

  return (
    <div className="fixed inset-0 z-[1300] flex flex-col bg-app-backdrop text-white">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-white/10 bg-app-overlay px-4 pb-3 backdrop-blur-md" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}>
        <button onClick={onClose} className="rounded-full p-2 transition-colors hover:bg-white/10" aria-label="Back">
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h2 className="text-lg font-bold">Share Event</h2>
          <p className="text-xs text-gray-400">Send this event in chat</p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/40">
              <CalendarIcon className="h-8 w-8 text-lime-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{event.name}</p>
              <p className="mt-1 line-clamp-3 text-sm text-gray-300">{event.description || 'No description'}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-1.5">
            <button
              onClick={handleShareElsewhere}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              Share elsewhere
            </button>
            {elsewhereFeedback && <p className="text-xs text-gray-400">{elsewhereFeedback}</p>}
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a user..."
              className="w-full rounded-xl border border-lime-500/30 bg-white px-4 py-3 text-black placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
            <div className="flex flex-col gap-2">
              {shareableUsers.length > 0 ? (
                shareableUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-3">
                      <img src={user.avatarUrl} alt={user.username} className="h-11 w-11 rounded-xl border border-lime-500/50 object-cover" />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{user.username}</p>
                        <p className="truncate text-sm text-gray-400">
                          {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'RIDAZ user'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSendShare(user.id)}
                      disabled={sentTo.has(user.id)}
                      className="rounded-lg bg-lime-500 px-4 py-2 text-sm font-bold text-black transition-colors hover:bg-lime-400 disabled:bg-gray-700 disabled:text-gray-400"
                    >
                      {sentTo.has(user.id) ? 'Sent' : 'Send'}
                    </button>
                  </div>
                ))
              ) : (
                <p className="py-6 text-center text-sm text-gray-400">No users found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareEventPage;
